import React, { useContext, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useParams } from 'react-router-dom';
import { removeAccessToken } from '../utils/api/authToken';
import { AuthContext } from '../utils/context/AuthContext';
import { canAccess } from '../utils/canAccess';
import { getNotificaions, updateNotificationStatus } from '../utils/api/api';
import toast from 'react-hot-toast';
import NotificationCard from './NotificationCard';
import logo from '../assets/miles_logo.svg';

// Icons
import {
  MdOutlineDashboardCustomize,
  MdPeople,
  MdAttachMoney,
  MdWorkOutline,
  MdTrendingUp,
  MdHelpOutline,
  MdSettings,
  MdCalendarToday,
  MdPayment,
  MdExpandMore,
  MdExpandLess,
  MdOutlineAssignment,
  MdHome,
  MdNotifications,
  MdClose,
  MdMenu,
  MdOutlineKeyboardArrowRight,
  MdLogout
} from 'react-icons/md';
import {
  IoMdNotificationsOutline,
  IoIosSearch,
  IoMdSunny
} from 'react-icons/io';
import { BiLogOutCircle } from 'react-icons/bi';
import { LiaFileContractSolid } from 'react-icons/lia';
import { FaRegCircleUser, FaCalculator, FaChevronDown } from 'react-icons/fa';

const TopMenu = ({ header }) => {
  const [isProjectsOpen, setIsProjectsOpen] = useState(false);
  const [isSalesOpen, setIsSalesOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationRef = useRef(null);
  const profileRef = useRef(null);

  const handleOutsideClick = (event) => {
    if (notificationRef.current && !notificationRef.current.contains(event.target)) {
      setIsNotificationsOpen(false);
    }
    if (profileRef.current && !profileRef.current.contains(event.target)) {
      setIsProfileOpen(false);
    }
  };

  const toggleReports = () => setIsReportsOpen(!isReportsOpen);

  useEffect(() => {
    if (isNotificationsOpen || isProfileOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isNotificationsOpen, isProfileOpen]);

  const location = useLocation();
  const { id } = useParams();
  const { user } = useContext(AuthContext);

  const payload = {
    user_id: Number(user?.id),
    is_admin: true,
    view_all: user?.role?.view_all_notifications,
  };

  const fetchNotifications = async () => {
    try {
      const response = await getNotificaions(payload);
      const readNotifications = response.notifications.filter((notif) => !notif.is_read);
      setNotifications(readNotifications);
      setUnreadCount(readNotifications.length);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await updateNotificationStatus(id);
      fetchNotifications();
      toast.success("Notification marked as read");
    } catch (err) {
      console.error("Failed to mark as read:", err);
      toast.error("Failed to update notification status");
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleProjects = () => {
    setIsProjectsOpen(!isProjectsOpen);
  };

  const toggleSales = () => {
    setIsSalesOpen(!isSalesOpen);
  };

  const toggleNotifications = () => {
    setIsNotificationsOpen(!isNotificationsOpen);
    setIsProfileOpen(false);
  };

  const toggleProfile = () => {
    setIsProfileOpen(!isProfileOpen);
    setIsNotificationsOpen(false);
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  const isActive = (paths) => paths.includes(location.pathname);

  const isSubmenuActive = (subPaths) => subPaths.some(path => location.pathname.startsWith(path));

  // Animation variants
  const sidebarVariants = {
    open: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.05
      }
    },
    closed: {
      x: '-100%',
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    }
  };

  const menuItemVariants = {
    open: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    },
    closed: {
      opacity: 0,
      x: -20,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    }
  };

  const dropdownVariants = {
    open: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    },
    closed: {
      opacity: 0,
      y: -10,
      scale: 0.95,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.first_name && !user?.last_name) return "U";
    return `${user?.first_name?.charAt(0) || ''}${user?.last_name?.charAt(0) || ''}`;
  };

  return (
    <>
      {/* Top Header Bar */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="h-16 bg-white/80 backdrop-blur-md border-b border-gray-100 shadow-sm sticky top-0 z-30 px-4 md:px-6"
      >
        <div className="flex items-center justify-between h-full max-w-screen-2xl mx-auto">
          {/* Left Section */}
          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleSidebar}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-700 flex md:hidden"
            >
              <MdMenu size={24} />
            </motion.button>

            <Link to="/dashboard" className="flex items-center gap-2">
              <img src={logo} className="h-9" alt="Logo" />
              <span className="font-semibold text-lg text-primary hidden md:block">Milescoop</span>
            </Link>
          </div>

          {/* Center Section */}
          <div className="hidden md:flex items-center">
            <h1 className="text-lg font-medium text-gray-800">{header}</h1>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-3">
            {/* Search Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-600 hidden md:flex"
            >
              <IoIosSearch size={22} />
            </motion.button>

            {/* Notifications */}
            <div className="relative" ref={notificationRef}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleNotifications}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-600 relative"
              >
                <IoMdNotificationsOutline size={24} />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </motion.button>

              <AnimatePresence>
                {isNotificationsOpen && (
                  <motion.div
                    initial="closed"
                    animate="open"
                    exit="closed"
                    variants={dropdownVariants}
                    className="absolute right-0 mt-2 w-80 bg-white/90 backdrop-blur-md shadow-lg rounded-2xl overflow-hidden z-50 border border-gray-100"
                  >
                    <div className="p-4 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-gray-800">Notifications</h3>
                        {notifications.length > 0 && (
                          <span className="bg-primary/10 text-primary text-xs font-medium px-2 py-1 rounded-full">
                            {notifications.length} new
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="max-h-[60vh] overflow-y-auto p-1">
                      {notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                          <div className="bg-gray-100 rounded-full p-3 mb-2">
                            <MdNotifications className="text-gray-400 text-xl" />
                          </div>
                          <p className="text-gray-500 text-sm">No new notifications</p>
                        </div>
                      ) : (
                        <ul>
                          {notifications.map((notification) => (
                            <NotificationCard
                              key={notification.id}
                              notification={notification}
                              onMarkAsRead={() => handleMarkAsRead(notification.id)}
                            />
                          ))}
                        </ul>
                      )}
                    </div>

                    {notifications.length > 0 && (
                      <div className="p-3 border-t border-gray-100">
                        <button
                          className="text-sm text-primary font-medium hover:text-primary/80 w-full text-center"
                          onClick={() => notifications.forEach(n => handleMarkAsRead(n.id))}
                        >
                          Mark all as read
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Profile Menu */}
            <div className="relative" ref={profileRef}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleProfile}
                className="flex items-center gap-2 cursor-pointer rounded-full py-1 px-2 hover:bg-gray-100"
              >
                <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
                  {getUserInitials()}
                </div>
                <span className="text-sm font-medium text-gray-800 hidden md:block">{user?.first_name}</span>
                <FaChevronDown size={12} className="text-gray-500 hidden md:block" />
              </motion.div>

              <AnimatePresence>
                {isProfileOpen && (
                  <motion.div
                    initial="closed"
                    animate="open"
                    exit="closed"
                    variants={dropdownVariants}
                    className="absolute right-0 mt-2 w-60 bg-white/90 backdrop-blur-md shadow-lg rounded-2xl overflow-hidden z-50 border border-gray-100"
                  >
                    <div className="p-4 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center text-lg font-medium">
                          {getUserInitials()}
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800">{user?.first_name} {user?.last_name}</h3>
                          <p className="text-gray-500 text-xs">{user?.email}</p>
                        </div>
                      </div>
                    </div>

                    <div className="p-2">
                      <Link to="/dashboard/settings" className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-gray-100 text-gray-700">
                        <MdSettings size={18} />
                        <span className="text-sm">Settings</span>
                      </Link>

                      <button
                        onClick={removeAccessToken}
                        className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-gray-100 text-gray-700 w-full text-left"
                      >
                        <MdLogout size={18} />
                        <span className="text-sm">Log out</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-40 md:hidden"
            onClick={toggleSidebar}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial="closed"
        animate={isSidebarOpen ? "open" : "closed"}
        variants={sidebarVariants}
        className="fixed top-0 left-0 h-full w-[280px] bg-gradient-to-b from-primary to-primary/95 text-white z-50 shadow-2xl md:shadow-none md:translate-x-0 md:z-20 p-0 overflow-hidden"
      >
        {/* Mobile Close Button */}
        <div className="flex items-center justify-between p-4 md:hidden">
          <div className="flex items-center gap-2">
            <img src={logo} className="h-9" alt="Logo" />
            <span className="font-semibold text-lg">Milescoop</span>
          </div>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={toggleSidebar}
            className="p-1.5 rounded-full hover:bg-white/10 text-white"
          >
            <MdClose size={20} />
          </motion.button>
        </div>

        {/* Sidebar Content */}
        <div className="h-full overflow-y-auto py-4 px-3">
          {/* User Profile Section */}
          <motion.div
            variants={menuItemVariants}
            className="mb-6 p-4 rounded-2xl bg-white/10 backdrop-blur-sm"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
                {getUserInitials()}
              </div>
              <div>
                <h3 className="font-medium text-white">{user?.first_name} {user?.last_name}</h3>
                <p className="text-white/70 text-xs truncate max-w-[180px]">{user?.email}</p>
              </div>
            </div>
          </motion.div>

          {/* Menu Categories */}
          <div className="space-y-1">
            <motion.p variants={menuItemVariants} className="px-4 text-xs uppercase tracking-wider text-white/50 font-medium mt-6 mb-2">
              Main
            </motion.p>

            <motion.div variants={menuItemVariants}>
              <Link
                to="/dashboard"
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${isActive(['/dashboard'])
                    ? 'bg-white/20 text-white font-medium'
                    : 'text-white/80 hover:bg-white/10'
                  }`}
              >
                <MdOutlineDashboardCustomize size={20} />
                <span>Dashboard</span>
              </Link>
            </motion.div>

            {canAccess(user, 'list_customer') && (
              <motion.div variants={menuItemVariants}>
                <Link
                  to="/dashboard/customers"
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${isActive(['/dashboard/customers', `/dashboard/customers/${id}/view`])
                      ? 'bg-white/20 text-white font-medium'
                      : 'text-white/80 hover:bg-white/10'
                    }`}
                >
                  <MdPeople size={20} />
                  <span>Customers</span>
                </Link>
              </motion.div>
            )}

            {canAccess(user, 'list_leads') && (
              <motion.div variants={menuItemVariants}>
                <Link
                  to="/dashboard/leads"
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${isActive(['/dashboard/leads', `/dashboard/leads/${id}/view`])
                      ? 'bg-white/20 text-white font-medium'
                      : 'text-white/80 hover:bg-white/10'
                    }`}
                >
                  <MdTrendingUp size={20} />
                  <span>Leads</span>
                </Link>
              </motion.div>
            )}

            <motion.p variants={menuItemVariants} className="px-4 text-xs uppercase tracking-wider text-white/50 font-medium mt-6 mb-2">
              Business
            </motion.p>

            <motion.div variants={menuItemVariants}>
              <button
                onClick={toggleSales}
                className={`flex items-center justify-between w-full px-4 py-2.5 rounded-xl text-sm transition-colors ${isSubmenuActive(['/dashboard/sales', '/dashboard/sales/customers', '/dashboard/sales/invoices'])
                    ? 'bg-white/20 text-white font-medium'
                    : 'text-white/80 hover:bg-white/10'
                  }`}
              >
                <div className="flex items-center gap-3">
                  <MdAttachMoney size={20} />
                  <span>Trends</span>
                </div>
                <motion.div
                  animate={{ rotate: isSalesOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <MdExpandMore size={20} />
                </motion.div>
              </button>

              <AnimatePresence>
                {isSalesOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="ml-4 pl-4 border-l border-white/20 overflow-hidden mt-1"
                  >
                    <Link
                      to="/dashboard/sales"
                      className={`flex items-center gap-3 px-4 py-2 rounded-xl text-sm my-1 transition-colors ${isActive(['/dashboard/sales'])
                          ? 'bg-white/10 text-white'
                          : 'text-white/70 hover:bg-white/5'
                        }`}
                    >
                      <span>Sales</span>
                    </Link>
                    <Link
                      to="/dashboard/sales/customers"
                      className={`flex items-center gap-3 px-4 py-2 rounded-xl text-sm my-1 transition-colors ${isActive(['/dashboard/sales/customers'])
                          ? 'bg-white/10 text-white'
                          : 'text-white/70 hover:bg-white/5'
                        }`}
                    >
                      <span>Customers</span>
                    </Link>
                    <Link
                      to="/dashboard/sales/invoices"
                      className={`flex items-center gap-3 px-4 py-2 rounded-xl text-sm my-1 transition-colors ${isActive(['/dashboard/sales/invoices'])
                          ? 'bg-white/10 text-white'
                          : 'text-white/70 hover:bg-white/5'
                        }`}
                    >
                      <span>Invoices</span>
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {canAccess(user, 'view_property') && (
              <motion.div variants={menuItemVariants}>
                <Link
                  to="/dashboard/properties"
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${isActive(['/dashboard/properties', '/dashboard/properties/new'])
                      ? 'bg-white/20 text-white font-medium'
                      : 'text-white/80 hover:bg-white/10'
                    }`}
                >
                  <MdHome size={20} />
                  <span>Properties</span>
                </Link>
              </motion.div>
            )}

            {canAccess(user, 'list_project') && (
              <motion.div variants={menuItemVariants}>
                <button
                  onClick={toggleProjects}
                  className={`flex items-center justify-between w-full px-4 py-2.5 rounded-xl text-sm transition-colors ${isSubmenuActive(['/dashboard/projects', 'dashboard/projects/new'])
                      ? 'bg-white/20 text-white font-medium'
                      : 'text-white/80 hover:bg-white/10'
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <MdWorkOutline size={20} />
                    <span>Projects</span>
                  </div>
                  <motion.div
                    animate={{ rotate: isProjectsOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <MdExpandMore size={20} />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {isProjectsOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="ml-4 pl-4 border-l border-white/20 overflow-hidden mt-1"
                    >
                      {canAccess(user, 'list_project') && (
                        <Link
                          to="/dashboard/projects/land"
                          className={`flex items-center gap-3 px-4 py-2 rounded-xl text-sm my-1 transition-colors ${isActive(['/dashboard/projects/land'])
                              ? 'bg-white/10 text-white'
                              : 'text-white/70 hover:bg-white/5'
                            }`}
                        >
                          <span>Land</span>
                        </Link>
                      )}
                      {canAccess(user, 'list_project') && (
                        <Link
                          to="/dashboard/projects/housing"
                          className={`flex items-center gap-3 px-4 py-2 rounded-xl text-sm my-1 transition-colors ${isActive(['/dashboard/projects/housing'])
                              ? 'bg-white/10 text-white'
                              : 'text-white/70 hover:bg-white/5'
                            }`}
                        >
                          <span>Housing</span>
                        </Link>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {canAccess(user, 'view_reports') && (
              <motion.div variants={menuItemVariants}>
                <button
                  onClick={toggleReports}
                  className={`flex items-center justify-between w-full px-4 py-2.5 rounded-xl text-sm transition-colors ${isSubmenuActive(['/dashboard/reports', '/dashboard/reports/payments', '/dashboard/reports/contracts', '/dashboard/reports/plots'])
                      ? 'bg-white/20 text-white font-medium'
                      : 'text-white/80 hover:bg-white/10'
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <MdHelpOutline size={20} />
                    <span>Reports</span>
                  </div>
                  <motion.div
                    animate={{ rotate: isReportsOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <MdExpandMore size={20} />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {isReportsOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="ml-4 pl-4 border-l border-white/20 overflow-hidden mt-1"
                    >
                      <Link
                        to="/dashboard/reports"
                        className={`flex items-center gap-3 px-4 py-2 rounded-xl text-sm my-1 transition-colors ${isActive(['/dashboard/reports/general'])
                            ? 'bg-white/10 text-white'
                            : 'text-white/70 hover:bg-white/5'
                          }`}
                      >
                        <span>General</span>
                      </Link>
                      <Link
                        to="/dashboard/reports/payments"
                        className={`flex items-center gap-3 px-4 py-2 rounded-xl text-sm my-1 transition-colors ${isActive(['/dashboard/reports/payments'])
                            ? 'bg-white/10 text-white'
                            : 'text-white/70 hover:bg-white/5'
                          }`}
                      >
                        <span>Payments</span>
                      </Link>
                      <Link
                        to="/dashboard/reports/contracts"
                        className={`flex items-center gap-3 px-4 py-2 rounded-xl text-sm my-1 transition-colors ${isActive(['/dashboard/reports/contracts'])
                            ? 'bg-white/10 text-white'
                            : 'text-white/70 hover:bg-white/5'
                          }`}
                      >
                        <span>Contracts</span>
                      </Link>
                      <Link
                        to="/dashboard/reports/plots"
                        className={`flex items-center gap-3 px-4 py-2 rounded-xl text-sm my-1 transition-colors ${isActive(['/dashboard/reports/plots'])
                            ? 'bg-white/10 text-white'
                            : 'text-white/70 hover:bg-white/5'
                          }`}
                      >
                        <span>Plots</span>
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            <motion.p variants={menuItemVariants} className="px-4 text-xs uppercase tracking-wider text-white/50 font-medium mt-6 mb-2">
              Management
            </motion.p>

            {canAccess(user, 'list_contract') && (
              <motion.div variants={menuItemVariants}>
                <Link
                  to="/dashboard/contracts"
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${isActive(['/dashboard/contracts', `/dashboard/contracts/${id}/view`])
                      ? 'bg-white/20 text-white font-medium'
                      : 'text-white/80 hover:bg-white/10'
                    }`}
                >
                  <LiaFileContractSolid size={20} />
                  <span>Contracts</span>
                </Link>
              </motion.div>
            )}

            {canAccess(user, 'list_payments') && (
              <motion.div variants={menuItemVariants}>
                <Link
                  to="/dashboard/payments"
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${isSubmenuActive(['/dashboard/payments'])
                      ? 'bg-white/20 text-white font-medium'
                      : 'text-white/80 hover:bg-white/10'
                    }`}
                >
                  <MdPayment size={20} />
                  <span>Payments</span>
                </Link>
              </motion.div>
            )}

            <motion.div variants={menuItemVariants}>
              <Link
                to="/dashboard/calendar"
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${isActive(['/dashboard/calendar', '/dashboard/calendar/site-visits'])
                    ? 'bg-white/20 text-white font-medium'
                    : 'text-white/80 hover:bg-white/10'
                  }`}
              >
                <MdCalendarToday size={20} />
                <span>Calendar</span>
              </Link>
            </motion.div>

            <motion.div variants={menuItemVariants}>
              <Link
                to="/dashboard/hr"
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${isActive(['/dashboard/hr', '/dashboard/hr/employees/add-new', `/dashboard/employees/${id}/view`])
                    ? 'bg-white/20 text-white font-medium'
                    : 'text-white/80 hover:bg-white/10'
                  }`}
              >
                <MdPeople size={20} />
                <span>Human Resource</span>
              </Link>
            </motion.div>

            {canAccess(user, 'list_tasks') && (
              <motion.div variants={menuItemVariants}>
                <Link
                  to="/dashboard/tasks"
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${isActive(['/dashboard/tasks'])
                      ? 'bg-white/20 text-white font-medium'
                      : 'text-white/80 hover:bg-white/10'
                    }`}
                >
                  <MdOutlineAssignment size={20} />
                  <span>Tasks</span>
                </Link>
              </motion.div>
            )}

            {canAccess(user, 'list_tickets') && (
              <motion.div variants={menuItemVariants}>
                <Link
                  to="/dashboard/tickets"
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${isActive(['/dashboard/tickets'])
                      ? 'bg-white/20 text-white font-medium'
                      : 'text-white/80 hover:bg-white/10'
                    }`}
                >
                  <LiaFileContractSolid size={20} />
                  <span>Tickets</span>
                </Link>
              </motion.div>
            )}

            <motion.div variants={menuItemVariants}>
              <Link
                to="/dashboard/calculator"
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${isActive(['/dashboard/calculator'])
                    ? 'bg-white/20 text-white font-medium'
                    : 'text-white/80 hover:bg-white/10'
                  }`}
              >
                <FaCalculator size={18} />
                <span>Calculator</span>
              </Link>
            </motion.div>

            {canAccess(user, 'view_settings') && (
              <motion.div variants={menuItemVariants}>
                <Link
                  to="/dashboard/settings"
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${isActive(['/dashboard/settings'])
                      ? 'bg-white/20 text-white font-medium'
                      : 'text-white/80 hover:bg-white/10'
                    }`}
                >
                  <MdSettings size={20} />
                  <span>Settings</span>
                </Link>
              </motion.div>
            )}

            <motion.div variants={menuItemVariants} className="mt-6">
              <button
                onClick={removeAccessToken}
                className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm w-full transition-colors text-white/80 hover:bg-white/10"
              >
                <BiLogOutCircle size={20} />
                <span>Log out</span>
              </button>
            </motion.div>
          </div>
        </div>
      </motion.aside>
    </>
  );
};

export default TopMenu;